import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, HostListener, inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, finalize, of, switchMap } from 'rxjs';
import { Httpservice } from '../../../services/httpservice';
import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { Commonservice } from '../../../services/commonservice';
import { DashboardHeaderComponent } from '../../structure/header/header';
import { DashboardSidebarComponent } from '../../structure/sidebar/sidebar';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { environment } from '../../../environments/environment';
import { ManageGeneratorAutocompleteComponent, ManageGeneratorAutocompleteCompleteEvent } from '../manage-generator/manage-generator-autocomplete';
import { createManageTransmissionLineFormValue, createManageTransmissionLineOpticalFiberFormValue, TRANSMISSION_LINE_BUNDLE_OPTIONS, TRANSMISSION_LINE_FIBER_TYPE_OPTIONS, TRANSMISSION_LINE_FIBER_USE_OPTIONS, TRANSMISSION_LINE_LOGSHEET_OPTIONS, TRANSMISSION_LINE_PARAMETER_OPTIONS } from './manage-transmission-lines.data';
import {
  ManageTransmissionLineFormValue,
  ManageTransmissionLineItem,
  ManageTransmissionLineLookupOption,
  ManageTransmissionLineOpticalFiberFormValue,
  ManageTransmissionLineOpticalFiberPayload,
  ManageTransmissionLinePagination,
  ManageTransmissionLinePayload,
} from './manage-transmission-lines.interfaces';

type ManageTransmissionLineModalMode = 'create' | 'edit' | 'view';
type ManageTransmissionLineSortField = 'line_name' | 'scada_code' | 'total_capacity';
type ManageTransmissionLineSortOrder = 'asc' | 'desc';
type ManageTransmissionLineFieldValue = string | number | boolean | null | undefined;
type ManageTransmissionLineFormFieldValue = string | number | null | undefined;
type ManageTransmissionLineQueryParams = Record<string, ManageTransmissionLineFieldValue>;
type TransmissionSubstationFilterKey = 'from_substation_id' | 'to_substation_id';

const TRANSMISSION_LINE_ALL_FILTER_VALUE = 'all';
const TRANSMISSION_LINE_NO_RECORDS_LABEL = 'No records';
const TRANSMISSION_LINE_SHOWING_RESULTS_LABEL = 'Showing';
const TRANSMISSION_LINE_RESULTS_LABEL = 'results';
const TRANSMISSION_LINE_ONLY_XLSX_MESSAGE = 'Only .xlsx files are allowed.';
const TRANSMISSION_LINE_REQUIRED_FIELDS_MESSAGE = 'Please fill in the required fields before saving.';
const TRANSMISSION_LINE_FAILED_LOAD_SUBSTATIONS_MESSAGE = 'Failed to load substations:';
const TRANSMISSION_LINE_FAILED_LOAD_VOLTAGE_LEVELS_MESSAGE = 'Failed to load voltage levels:';
const TRANSMISSION_LINE_FAILED_LOAD_LINES_MESSAGE = 'Failed to load transmission lines:';
const TRANSMISSION_LINE_FAILED_EXPORT_MESSAGE = 'Failed to export transmission lines.';
const TRANSMISSION_LINE_FAILED_TEMPLATE_MESSAGE = 'Failed to download template.';
const TRANSMISSION_LINE_FAILED_ATTACHMENT_MESSAGE = 'Transmission line saved, but the file could not be attached.';
const TRANSMISSION_LINE_FAILED_SAVE_MESSAGE = 'Failed to save transmission line.';

@Component({
  selector: 'app-manage-transmission-lines-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, DashboardSidebarComponent, DashboardHeaderComponent, ManageGeneratorAutocompleteComponent],
  templateUrl: './manage-transmission-lines.html',
  styleUrl: './manage-transmission-lines.scss',
})
export class ManageTransmissionLinesPage implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly bundleOptions = TRANSMISSION_LINE_BUNDLE_OPTIONS;
  protected readonly fiberTypeOptions = TRANSMISSION_LINE_FIBER_TYPE_OPTIONS;
  protected readonly fiberUseOptions = TRANSMISSION_LINE_FIBER_USE_OPTIONS;
  protected readonly parameterOptions = TRANSMISSION_LINE_PARAMETER_OPTIONS;
  protected readonly logsheetOptions = TRANSMISSION_LINE_LOGSHEET_OPTIONS;
  protected readonly pageSizeOptions = [15, 25, 50, 100];
  protected readonly pageSizeControl: FormControl;

  protected readonly filterForm: UntypedFormGroup;
  protected readonly transmissionLineForm: UntypedFormGroup;

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected isExporting = false;
  protected isDownloadingTemplate = false;
  protected isImporting = false;
  protected isTransmissionActionMenuOpen = false;
  protected modalOpen = false;
  protected deleteModalOpen = false;
  protected modalMode: ManageTransmissionLineModalMode = 'create';
  protected selectedTransmissionLine: ManageTransmissionLineItem | null = null;
  protected formSubmitted = false;
  protected sortBy: ManageTransmissionLineSortField | null = null;
  protected sortOrder: ManageTransmissionLineSortOrder = 'asc';

  protected lines: ManageTransmissionLineItem[] = [];
  protected filteredLines: ManageTransmissionLineItem[] = [];
  protected pagination: ManageTransmissionLinePagination = {
    page_no: 1,
    page_size: 15,
    total_records: 0,
    total_pages: 0,
  };

  protected substationOptions: ManageTransmissionLineLookupOption[] = [];
  protected filteredFromSubstationOptions: ManageTransmissionLineLookupOption[] = [];
  protected filteredToSubstationOptions: ManageTransmissionLineLookupOption[] = [];
  protected filteredModalFromSubstationOptions: ManageTransmissionLineLookupOption[] = [];
  protected filteredModalToSubstationOptions: ManageTransmissionLineLookupOption[] = [];
  protected voltageLevelOptions: ManageTransmissionLineLookupOption[] = [];
  protected fromSubstationSearchTerm = '';
  protected toSubstationSearchTerm = '';
  protected parameterFilterDropdownOpen = false;
  protected parameterDropdownOpen = false;
  protected modalFromSubstationSearchTerm = '';
  protected modalToSubstationSearchTerm = '';
  protected selectedFile: File | null = null;
  protected selectedFileName = '';
  protected selectedFilePreviewUrl: string | null = null;
  protected existingFilePath: string | null = null;
  protected filePathCleared = false;
  protected submitErrorMessage = '';
  private lastAutogeneratedTransmissionLineName = '';

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
    private readonly commonSvc: Commonservice,
  ) {
    this.filterForm = this.fb.group({
      search: [''],
      from_substation_id: [TRANSMISSION_LINE_ALL_FILTER_VALUE],
      to_substation_id: [TRANSMISSION_LINE_ALL_FILTER_VALUE],
      voltage_level_id: [TRANSMISSION_LINE_ALL_FILTER_VALUE],
      parameter: [[]],
      page_size: [15],
    });
    this.pageSizeControl = this.filterForm.get('page_size') as FormControl;

    this.transmissionLineForm = this.fb.group({
      from_substation_id: [null],
      to_substation_id: [null],
      voltage_level_id: [null, Validators.required],
      transmission_line_name: [{ value: '', disabled: false }],
      scada_code: ['', [Validators.maxLength(255)]],
      parameter: [[]],
      logsheet: [true],
      no_of_circuit: ['', [Validators.maxLength(10)]],
      length_km: ['', [Validators.maxLength(20)]],
      circuit_km: [{ value: '', disabled: false }],
      per_circuit_ampere: ['', [Validators.maxLength(20)]],
      per_circuit_mva: ['', [Validators.maxLength(20)]],
      total_capacity_mva: [{ value: '', disabled: false }],
      conductor_code_name: ['', [Validators.maxLength(255)]],
      bundle: ['', [Validators.maxLength(100)]],
      conductor_type: ['', [Validators.maxLength(255)]],
      no_of_towers: ['', [Validators.maxLength(10)]],
      starting_tower_no: ['', [Validators.maxLength(100)]],
      ending_tower_no: ['', [Validators.maxLength(100)]],
      is_crossborder: [false],
      optical_fiber_details: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.loadLookupData();
    this.loadTransmissionLines(1);
    this.resetOpticalFiberRows();

    this.filterForm.valueChanges
      .pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadTransmissionLines(1));

    this.transmissionLineForm.get('from_substation_id')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncComputedFields());

    this.transmissionLineForm.get('to_substation_id')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncComputedFields());

    this.transmissionLineForm.get('voltage_level_id')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncComputedFields());

    this.transmissionLineForm.get('no_of_circuit')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncComputedFields());

    this.transmissionLineForm.get('length_km')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncComputedFields());

    this.transmissionLineForm.get('per_circuit_mva')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncComputedFields());

    this.transmissionLineForm.get('is_crossborder')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncCrossborderParameterState());
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected clearFilters(): void {
    this.filterForm.reset(
      {
        search: '',
        from_substation_id: TRANSMISSION_LINE_ALL_FILTER_VALUE,
        to_substation_id: TRANSMISSION_LINE_ALL_FILTER_VALUE,
        voltage_level_id: TRANSMISSION_LINE_ALL_FILTER_VALUE,
        parameter: [],
        page_size: this.pageSizeControl.value ?? 15,
      },
      { emitEvent: false },
    );
    this.isTransmissionActionMenuOpen = false;
    this.fromSubstationSearchTerm = '';
    this.toSubstationSearchTerm = '';
    this.filteredFromSubstationOptions = [...this.substationOptions];
    this.filteredToSubstationOptions = [...this.substationOptions];
    this.loadTransmissionLines(1);
  }

  protected getParameterFilterDisplayValue(): string {
    const selections = this.getParameterFilterValues();
    return this.parameterOptions
      .filter((option) => selections.includes(option.value))
      .map((option) => option.label)
      .join(', ');
  }

  protected toggleParameterFilterDropdown(): void {
    this.parameterFilterDropdownOpen = !this.parameterFilterDropdownOpen;
  }

  protected isParameterFilterSelected(value: string): boolean {
    return this.getParameterFilterValues().includes(value);
  }

  protected onParameterFilterSelection(value: string, checked: boolean): void {
    const selections = this.getParameterFilterValues();
    const nextSelections = checked
      ? [...new Set([...selections, value])]
      : selections.filter((selection) => selection !== value);
    this.filterForm.get('parameter')?.setValue(nextSelections);
  }

  protected getParameterDisplayValue(): string {
    const values = this.transmissionLineForm.get('parameter')?.value;
    if (!Array.isArray(values)) {
      return '';
    }
    return this.parameterOptions
      .filter((option) => values.includes(option.value))
      .map((option) => option.label)
      .join(', ');
  }

  protected toggleParameterDropdown(): void {
    if (this.modalMode !== 'view' && !this.isCrossborderSelected(true)) {
      this.parameterDropdownOpen = !this.parameterDropdownOpen;
    }
  }

  protected isParameterSelected(value: string): boolean {
    const selections = this.transmissionLineForm.get('parameter')?.value;
    return Array.isArray(selections) && selections.includes(value);
  }

  protected onParameterSelection(value: string, checked: boolean): void {
    if (this.modalMode === 'view' || this.isCrossborderSelected(true)) {
      return;
    }
    const control = this.transmissionLineForm.get('parameter');
    const selections = Array.isArray(control?.value) ? control.value : [];
    const nextSelections = checked
      ? [...new Set([...selections, value])]
      : selections.filter((selection: string) => selection !== value);
    control?.setValue(nextSelections);
  }

  @HostListener('document:mousedown', ['$event'])
  protected closeParameterDropdowns(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.manage-transmission-parameter-dropdown')) {
      return;
    }
    this.parameterFilterDropdownOpen = false;
    this.parameterDropdownOpen = false;
  }

  protected loadLookupData(): void {
    this.loadSubstations();
    this.loadVoltageLevels();
  }

  protected loadSubstations(): void {
    this.httpSvc
      .requestCall(AuthEndPoints.GET_TRANSMISSION_LINES_SUBSTATIONS_LIST, ApiMethod.GET, '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.substationOptions = (res?.value?.substations ?? res?.substations ?? []).map((item: any) => ({
            id: item.id,
            name: item.station_name,
            display_name: item.option_type === 'generator' ? `${item.station_name} (Generator)` : item.station_name,
            station_code: item.station_code ?? null,
            option_type: item.option_type ?? 'substation',
          }));
          this.filteredFromSubstationOptions = [...this.substationOptions];
          this.filteredToSubstationOptions = [...this.substationOptions];
          this.filteredModalFromSubstationOptions = [...this.substationOptions];
          this.filteredModalToSubstationOptions = [...this.substationOptions];
          this.syncComputedFields();
        },
        error: (err) => {
          console.error(TRANSMISSION_LINE_FAILED_LOAD_SUBSTATIONS_MESSAGE, err);
          this.substationOptions = [];
          this.filteredFromSubstationOptions = [];
          this.filteredToSubstationOptions = [];
          this.filteredModalFromSubstationOptions = [];
          this.filteredModalToSubstationOptions = [];
        },
      });
  }

  protected onFromSubstationSearchInput(event: ManageGeneratorAutocompleteCompleteEvent): void {
    this.handleSubstationFilterSearch('from_substation_id', event);
  }

  protected onToSubstationSearchInput(event: ManageGeneratorAutocompleteCompleteEvent): void {
    this.handleSubstationFilterSearch('to_substation_id', event);
  }

  protected onFromSubstationSearchBlur(): void {
    this.commitSubstationFilterAutocompleteValue('from_substation_id');
  }

  protected onToSubstationSearchBlur(): void {
    this.commitSubstationFilterAutocompleteValue('to_substation_id');
  }

  private handleSubstationFilterSearch(fieldName: TransmissionSubstationFilterKey, event: ManageGeneratorAutocompleteCompleteEvent): void {
    const value = event?.query ?? '';
    this.setSubstationSearchTerm(fieldName, value);
    this.filterSubstationAutocomplete(fieldName, value);

    const normalizedValue = value.trim();
    if (!normalizedValue) {
      this.clearSubstationFilterSelection(fieldName);
      this.loadTransmissionLines(1);
      return;
    }

    const match = this.findSubstationByName(normalizedValue);
    if (!match) {
      return;
    }

    this.applySubstationFilterSelection(fieldName, match);
  }

  private commitSubstationFilterAutocompleteValue(fieldName: TransmissionSubstationFilterKey): void {
    const trimmedValue = this.getSubstationSearchTerm(fieldName).trim();
    if (!trimmedValue) {
      this.clearSubstationFilterSelection(fieldName);
      this.loadTransmissionLines(1);
      return;
    }

    const match = this.findSubstationByName(trimmedValue);
    if (!match) {
      this.clearSubstationFilterSelection(fieldName);
      this.loadTransmissionLines(1);
      return;
    }

    this.applySubstationFilterSelection(fieldName, match);
  }

  private applySubstationFilterSelection(fieldName: TransmissionSubstationFilterKey, substation: ManageTransmissionLineLookupOption): void {
    const displayName = substation.display_name ?? substation.name;
    if (this.filterForm.get(fieldName)?.value === substation.id && this.getSubstationSearchTerm(fieldName) === displayName) {
      return;
    }

    this.setSubstationSearchTerm(fieldName, displayName);
    this.filterForm.patchValue({ [fieldName]: substation.id }, { emitEvent: false });
    this.filterSubstationAutocomplete(fieldName, displayName);
    this.loadTransmissionLines(1);
  }

  private clearSubstationFilterSelection(fieldName: TransmissionSubstationFilterKey): void {
    const currentValue = this.filterForm.get(fieldName)?.value;
    this.setSubstationSearchTerm(fieldName, '');
    this.filterSubstationAutocomplete(fieldName, '');
    if (currentValue === TRANSMISSION_LINE_ALL_FILTER_VALUE) {
      return;
    }

    this.filterForm.patchValue({ [fieldName]: TRANSMISSION_LINE_ALL_FILTER_VALUE }, { emitEvent: false });
  }

  private filterSubstationAutocomplete(fieldName: TransmissionSubstationFilterKey, query: string): void {
    const normalizedQuery = query.trim().toLowerCase();
    const filteredOptions = !normalizedQuery
      ? [...this.substationOptions]
      : this.substationOptions.filter((substation) => this.isSubstationOptionMatch(substation, normalizedQuery));

    if (fieldName === 'from_substation_id') {
      this.filteredFromSubstationOptions = filteredOptions;
      return;
    }

    this.filteredToSubstationOptions = filteredOptions;
  }

  private findSubstationByName(name: string): ManageTransmissionLineLookupOption | undefined {
    const normalizedName = name.trim().toLowerCase();
    if (!normalizedName) {
      return undefined;
    }

    return this.substationOptions.find(
      (substation) =>
        (substation.display_name ?? '').trim().toLowerCase() === normalizedName ||
        substation.name.trim().toLowerCase() === normalizedName ||
        (substation.station_code ?? '').trim().toLowerCase() === normalizedName,
    );
  }

  private findSubstationOptionBySelection(
    substationId: number | string | null | undefined,
    stationCode?: string | null,
  ): ManageTransmissionLineLookupOption | undefined {
    const numericId = Number(substationId);
    const normalizedCode = (stationCode ?? '').trim().toLowerCase();
    if (normalizedCode) {
      const matchByCode = this.substationOptions.find(
        (substation) => substation.id === numericId && (substation.station_code ?? '').trim().toLowerCase() === normalizedCode,
      );
      if (matchByCode) {
        return matchByCode;
      }
    }

    return this.substationOptions.find((substation) => substation.id === numericId);
  }

  private getSelectedSubstationCode(fieldName: TransmissionSubstationFilterKey): string | null {
    const selectedId = this.transmissionLineForm.get(fieldName)?.value;
    const selectedName = this.getModalSubstationSearchTerm(fieldName);
    const option = this.findSubstationByName(selectedName) ?? this.findSubstationOptionBySelection(selectedId);
    return option?.station_code ?? null;
  }

  private isSubstationOptionMatch(substation: ManageTransmissionLineLookupOption, normalizedQuery: string): boolean {
    return (
      substation.name.toLowerCase().includes(normalizedQuery) ||
      (substation.display_name ?? '').toLowerCase().includes(normalizedQuery) ||
      (substation.station_code ?? '').toLowerCase().includes(normalizedQuery)
    );
  }

  private getSubstationSearchTerm(fieldName: TransmissionSubstationFilterKey): string {
    return fieldName === 'from_substation_id' ? this.fromSubstationSearchTerm : this.toSubstationSearchTerm;
  }

  private setSubstationSearchTerm(fieldName: TransmissionSubstationFilterKey, value: string): void {
    if (fieldName === 'from_substation_id') {
      this.fromSubstationSearchTerm = value;
      return;
    }

    this.toSubstationSearchTerm = value;
  }

  protected onModalFromSubstationSearchInput(event: ManageGeneratorAutocompleteCompleteEvent): void {
    this.handleModalSubstationSearch('from_substation_id', event);
  }

  protected onModalToSubstationSearchInput(event: ManageGeneratorAutocompleteCompleteEvent): void {
    this.handleModalSubstationSearch('to_substation_id', event);
  }

  protected onModalFromSubstationSelect(event: { value?: ManageTransmissionLineLookupOption | string }): void {
    this.applySelectedModalSubstation('from_substation_id', event?.value);
  }

  protected onModalToSubstationSelect(event: { value?: ManageTransmissionLineLookupOption | string }): void {
    this.applySelectedModalSubstation('to_substation_id', event?.value);
  }

  protected onModalFromSubstationSearchBlur(): void {
    this.commitModalSubstationAutocompleteValue('from_substation_id');
  }

  protected onModalToSubstationSearchBlur(): void {
    this.commitModalSubstationAutocompleteValue('to_substation_id');
  }

  private handleModalSubstationSearch(fieldName: TransmissionSubstationFilterKey, event: ManageGeneratorAutocompleteCompleteEvent): void {
    const value = event?.query ?? '';
    this.setModalSubstationSearchTerm(fieldName, value);
    this.filterModalSubstationAutocomplete(fieldName, value);

    const match = this.findSubstationByName(value);
    if (match) {
      this.applyModalSubstationSelection(fieldName, match);
      return;
    }

    if (!value.trim()) {
      this.clearModalSubstationSelection(fieldName);
    }
  }

  private commitModalSubstationAutocompleteValue(fieldName: TransmissionSubstationFilterKey): void {
    const trimmedValue = this.getModalSubstationSearchTerm(fieldName).trim();
    if (!trimmedValue) {
      this.clearModalSubstationSelection(fieldName);
      return;
    }

    const match = this.findSubstationByName(trimmedValue);
    if (!match) {
      this.clearModalSubstationSelection(fieldName);
      return;
    }

    this.applyModalSubstationSelection(fieldName, match);
  }

  private applyModalSubstationSelection(fieldName: TransmissionSubstationFilterKey, substation: ManageTransmissionLineLookupOption): void {
    this.setModalSubstationSearchTerm(fieldName, substation.display_name ?? substation.name);
    this.transmissionLineForm.patchValue({ [fieldName]: substation.id }, { emitEvent: true });
    this.filterModalSubstationAutocomplete(fieldName, substation.display_name ?? substation.name);
  }

  private applySelectedModalSubstation(
    fieldName: TransmissionSubstationFilterKey,
    value?: ManageTransmissionLineLookupOption | string,
  ): void {
    const substation = typeof value === 'object' ? value : this.findSubstationByName(value ?? '');
    if (substation) {
      this.applyModalSubstationSelection(fieldName, substation);
    }
  }

  private clearModalSubstationSelection(fieldName: TransmissionSubstationFilterKey): void {
    this.setModalSubstationSearchTerm(fieldName, '');
    this.transmissionLineForm.patchValue({ [fieldName]: null }, { emitEvent: true });
    this.filterModalSubstationAutocomplete(fieldName, '');
  }

  private filterModalSubstationAutocomplete(fieldName: TransmissionSubstationFilterKey, query: string): void {
    const normalizedQuery = query.trim().toLowerCase();
    const filteredOptions = !normalizedQuery
      ? [...this.substationOptions]
      : this.substationOptions.filter((substation) => this.isSubstationOptionMatch(substation, normalizedQuery));

    if (fieldName === 'from_substation_id') {
      this.filteredModalFromSubstationOptions = filteredOptions;
      return;
    }

    this.filteredModalToSubstationOptions = filteredOptions;
  }

  private getModalSubstationSearchTerm(fieldName: TransmissionSubstationFilterKey): string {
    return fieldName === 'from_substation_id' ? this.modalFromSubstationSearchTerm : this.modalToSubstationSearchTerm;
  }

  private setModalSubstationSearchTerm(fieldName: TransmissionSubstationFilterKey, value: string): void {
    if (fieldName === 'from_substation_id') {
      this.modalFromSubstationSearchTerm = value;
      return;
    }

    this.modalToSubstationSearchTerm = value;
  }

  private syncModalSubstationSearchTerms(): void {
    const fromSubstationId = this.transmissionLineForm.get('from_substation_id')?.value;
    const toSubstationId = this.transmissionLineForm.get('to_substation_id')?.value;
    this.modalFromSubstationSearchTerm = this.findSubstationOptionBySelection(fromSubstationId, this.selectedTransmissionLine?.from_substation_code)?.display_name ?? '';
    this.modalToSubstationSearchTerm = this.findSubstationOptionBySelection(toSubstationId, this.selectedTransmissionLine?.to_substation_code)?.display_name ?? '';
    this.filterModalSubstationAutocomplete('from_substation_id', this.modalFromSubstationSearchTerm);
    this.filterModalSubstationAutocomplete('to_substation_id', this.modalToSubstationSearchTerm);
  }

  protected setCrossborderFlag(value: boolean): void {
    if (this.modalMode === 'view') {
      return;
    }

    this.transmissionLineForm.patchValue({ is_crossborder: value }, { emitEvent: true });
  }

  protected isCrossborderSelected(value: boolean): boolean {
    return Boolean(this.transmissionLineForm.get('is_crossborder')?.value) === value;
  }

  private syncCrossborderParameterState(): void {
    const parameterControl = this.transmissionLineForm.get('parameter');
    if (!parameterControl) {
      return;
    }

    const isCrossborder = Boolean(this.transmissionLineForm.get('is_crossborder')?.value);
    if (isCrossborder) {
      parameterControl.disable({ emitEvent: false });
      parameterControl.setValue([], { emitEvent: false });
      return;
    }

    if (this.modalMode !== 'view') {
      parameterControl.enable({ emitEvent: false });
    }
  }

  protected loadVoltageLevels(): void {
    this.httpSvc
      .requestCall(AuthEndPoints.GET_VOLTAGE_LEVELS_LIST, ApiMethod.GET, '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.voltageLevelOptions = (res?.value?.voltage_levels ?? res?.voltage_levels ?? []).map((item: any) => ({
            id: item.id,
            name: `${item.level}${item.unit}`,
          }));
          this.syncComputedFields();
        },
        error: (err) => {
          console.error(TRANSMISSION_LINE_FAILED_LOAD_VOLTAGE_LEVELS_MESSAGE, err);
          this.voltageLevelOptions = [];
        },
      });
  }

  protected loadTransmissionLines(pageNo = this.pagination.page_no): void {
    this.isLoading = true;
    const query = this.buildQueryString({
      search: this.filterForm.get('search')?.value,
      from_substation_id: this.filterForm.get('from_substation_id')?.value,
      to_substation_id: this.filterForm.get('to_substation_id')?.value,
      voltage_level_id: this.filterForm.get('voltage_level_id')?.value,
      parameter: this.getParameterFilterValue(),
      page_no: pageNo,
      page_size: this.filterForm.get('page_size')?.value,
      sort_by: this.sortBy,
      sort_order: this.sortBy ? this.sortOrder : null,
    });

    this.httpSvc
      .requestCall(AuthEndPoints.GET_TRANSMISSION_LINES_LIST, ApiMethod.GET, query)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res) => {
          this.lines = res?.value?.transmission_lines ?? res?.transmission_lines ?? [];
          this.filteredLines = [...this.lines];
          this.pagination = this.extractPagination(res?.value) ?? this.pagination;
        },
        error: (err) => {
          console.error(TRANSMISSION_LINE_FAILED_LOAD_LINES_MESSAGE, err);
          this.lines = [];
          this.filteredLines = [];
          this.pagination = {
            page_no: pageNo,
            page_size: Number(this.filterForm.get('page_size')?.value ?? 10),
            total_records: 0,
            total_pages: 0,
          };
        },
      });
  }

  protected openAddTransmissionLineModal(): void {
    this.isTransmissionActionMenuOpen = false;
    this.modalMode = 'create';
    this.selectedTransmissionLine = null;
    this.lastAutogeneratedTransmissionLineName = '';
    this.enableTransmissionLineForm();
    this.setSelectedFile(null);
    this.selectedFileName = '';
    this.existingFilePath = null;
    this.filePathCleared = false;
    this.submitErrorMessage = '';
    this.transmissionLineForm.reset(createManageTransmissionLineFormValue(), { emitEvent: false });
    this.resetOpticalFiberRows();
    this.formSubmitted = false;
    this.modalOpen = true;
    this.syncModalSubstationSearchTerms();
    this.syncCrossborderParameterState();
    this.syncComputedFields();
  }

  protected openEditTransmissionLineModal(item: ManageTransmissionLineItem): void {
    this.isTransmissionActionMenuOpen = false;
    this.modalMode = 'edit';
    this.selectedTransmissionLine = item;
    this.lastAutogeneratedTransmissionLineName = '';
    this.enableTransmissionLineForm();
    this.setSelectedFile(null);
    this.selectedFileName = item.file_path ? this.getFileName(item.file_path) : '';
    this.existingFilePath = item.file_path;
    this.filePathCleared = false;
    this.submitErrorMessage = '';
    this.transmissionLineForm.reset(
      {
        from_substation_id: item.from_substation_id,
        to_substation_id: item.to_substation_id,
        voltage_level_id: item.voltage_level_id,
        transmission_line_name: item.transmission_line_name,
        scada_code: item.scada_code ?? '',
        parameter: this.splitParameterValues(item.parameter),
        logsheet: item.logsheet,
        no_of_circuit: this.toFormString(item.no_of_circuit),
        length_km: this.toFormString(item.length_km),
        circuit_km: this.toFormString(item.circuit_km),
        per_circuit_ampere: this.toFormString(item.per_circuit_ampere),
        per_circuit_mva: this.toFormString(item.per_circuit_mva),
        total_capacity_mva: this.toFormString(item.total_capacity_mva),
        conductor_code_name: item.conductor_code_name ?? '',
        bundle: item.bundle ?? '',
        conductor_type: item.conductor_type ?? '',
        no_of_towers: this.toFormString(item.no_of_towers),
        starting_tower_no: item.starting_tower_no ?? '',
        ending_tower_no: item.ending_tower_no ?? '',
        is_crossborder: item.is_crossborder,
        optical_fiber_details: [],
      },
      { emitEvent: false },
    );
    this.lastAutogeneratedTransmissionLineName = this.buildAutogeneratedTransmissionLineName();
    this.resetOpticalFiberRows(item.optical_fiber_details);
    this.formSubmitted = false;
    this.modalOpen = true;
    this.syncModalSubstationSearchTerms();
    this.syncCrossborderParameterState();
    this.syncComputedFields();
  }

  protected openViewTransmissionLineModal(item: ManageTransmissionLineItem): void {
    this.isTransmissionActionMenuOpen = false;
    this.modalMode = 'view';
    this.selectedTransmissionLine = item;
    this.lastAutogeneratedTransmissionLineName = '';
    this.enableTransmissionLineForm();
    this.setSelectedFile(null);
    this.selectedFileName = item.file_path ? this.getFileName(item.file_path) : '';
    this.existingFilePath = item.file_path;
    this.filePathCleared = false;
    this.submitErrorMessage = '';
    this.transmissionLineForm.reset(
      {
        from_substation_id: item.from_substation_id,
        to_substation_id: item.to_substation_id,
        voltage_level_id: item.voltage_level_id,
        transmission_line_name: item.transmission_line_name,
        scada_code: item.scada_code ?? '',
        parameter: this.splitParameterValues(item.parameter),
        logsheet: item.logsheet,
        no_of_circuit: this.toFormString(item.no_of_circuit),
        length_km: this.toFormString(item.length_km),
        circuit_km: this.toFormString(item.circuit_km),
        per_circuit_ampere: this.toFormString(item.per_circuit_ampere),
        per_circuit_mva: this.toFormString(item.per_circuit_mva),
        total_capacity_mva: this.toFormString(item.total_capacity_mva),
        conductor_code_name: item.conductor_code_name ?? '',
        bundle: item.bundle ?? '',
        conductor_type: item.conductor_type ?? '',
        no_of_towers: this.toFormString(item.no_of_towers),
        starting_tower_no: item.starting_tower_no ?? '',
        ending_tower_no: item.ending_tower_no ?? '',
        is_crossborder: item.is_crossborder,
        optical_fiber_details: [],
      },
      { emitEvent: false },
    );
    this.lastAutogeneratedTransmissionLineName = this.buildAutogeneratedTransmissionLineName();
    this.resetOpticalFiberRows(item.optical_fiber_details);
    this.formSubmitted = false;
    this.modalOpen = true;
    this.syncModalSubstationSearchTerms();
    this.syncCrossborderParameterState();
    this.disableTransmissionLineForm();
    this.syncComputedFields();
  }

  protected openDeleteTransmissionLineModal(item: ManageTransmissionLineItem): void {
    this.isTransmissionActionMenuOpen = false;
    this.selectedTransmissionLine = item;
    this.deleteModalOpen = true;
  }

  protected closeTransmissionLineModal(): void {
    this.isTransmissionActionMenuOpen = false;
    this.modalOpen = false;
    this.selectedTransmissionLine = null;
    this.modalMode = 'create';
    this.enableTransmissionLineForm();
    this.setSelectedFile(null);
    this.selectedFileName = '';
    this.existingFilePath = null;
    this.filePathCleared = false;
    this.submitErrorMessage = '';
    this.transmissionLineForm.reset(createManageTransmissionLineFormValue(), { emitEvent: false });
    this.syncModalSubstationSearchTerms();
    this.syncCrossborderParameterState();
    this.resetOpticalFiberRows();
    this.formSubmitted = false;
  }

  protected closeDeleteModal(): void {
    this.deleteModalOpen = false;
    this.selectedTransmissionLine = null;
  }

  protected toggleTransmissionActionMenu(): void {
    if (this.isLoading) {
      return;
    }

    this.isTransmissionActionMenuOpen = !this.isTransmissionActionMenuOpen;
  }

  protected exportTransmissionLineData(): void {
    this.isTransmissionActionMenuOpen = false;
    this.isExporting = true;
    const query = this.buildQueryString({
      search: this.filterForm.get('search')?.value,
      from_substation_id: this.filterForm.get('from_substation_id')?.value,
      to_substation_id: this.filterForm.get('to_substation_id')?.value,
      voltage_level_id: this.filterForm.get('voltage_level_id')?.value,
      parameter: this.getParameterFilterValue(),
      page_no: this.pagination.page_no,
      page_size: this.pagination.page_size,
    });

    this.httpSvc
      .downloadfile(AuthEndPoints.EXPORT_TRANSMISSION_LINES, ApiMethod.GET, query)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isExporting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (blob) => this.downloadBlob(blob, this.buildTransmissionLineFileName('transmission_lines_export')),
        error: (error) => this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || TRANSMISSION_LINE_FAILED_EXPORT_MESSAGE),
      });
  }

  protected downloadTransmissionLineTemplate(): void {
    this.isTransmissionActionMenuOpen = false;
    this.isDownloadingTemplate = true;
    this.httpSvc
      .downloadfile(AuthEndPoints.TRANSMISSION_LINE_TEMPLATE, ApiMethod.GET)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isDownloadingTemplate = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (blob) => this.downloadBlob(blob, 'transmission_lines_import_template.xlsx'),
        error: (error) => this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || TRANSMISSION_LINE_FAILED_TEMPLATE_MESSAGE),
      });
  }

  protected triggerTransmissionImportFilePicker(fileInput: HTMLInputElement | null): void {
    if (!fileInput || this.isLoading || this.isExporting || this.isDownloadingTemplate || this.isImporting) {
      return;
    }

    this.isTransmissionActionMenuOpen = false;
    fileInput.click();
  }

  protected onTransmissionImportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.length ? input.files[0] : null;

    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      this.commonSvc.showErrorToastr(TRANSMISSION_LINE_ONLY_XLSX_MESSAGE);
      if (input) {
        input.value = '';
      }
      return;
    }

    this.isImporting = true;
    const formData = new FormData();
    formData.append('file', file);

    this.httpSvc
      .requestCall(AuthEndPoints.IMPORT_TRANSMISSION_LINES, ApiMethod.POST, formData, true)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isImporting = false;
          if (input) {
            input.value = '';
          }
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => this.loadTransmissionLines(this.pagination.page_no),
      });
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.length ? input.files[0] : null;
    this.setSelectedFile(file);
  }

  protected removeSelectedFile(fileInput?: HTMLInputElement | null): void {
    if (this.modalMode === 'view') {
      return;
    }

    this.setSelectedFile(null);
    this.filePathCleared = this.modalMode === 'edit';
    if (this.modalMode === 'edit') {
      this.existingFilePath = null;
    }
    if (fileInput) {
      fileInput.value = '';
    }
  }

  protected previewSelectedFile(): void {
    const previewUrl = this.getFilePreviewUrl();
    if (!previewUrl || typeof window === 'undefined') {
      return;
    }

    window.open(previewUrl, '_blank', 'noopener');
  }

  protected hasFilePreview(): boolean {
    return Boolean(this.getFilePreviewUrl());
  }

  protected getFilePreviewLabel(): string {
    if (this.selectedFileName) {
      return this.selectedFileName;
    }

    if (this.existingFilePath) {
      return this.getFileName(this.existingFilePath);
    }

    return 'No file selected';
  }

  protected hasAnyFile(): boolean {
    return Boolean(this.selectedFile || this.existingFilePath);
  }

  protected get opticalFiberDetailsArray(): UntypedFormArray {
    return this.transmissionLineForm.get('optical_fiber_details') as UntypedFormArray;
  }

  protected get opticalFiberControls(): UntypedFormGroup[] {
    return this.opticalFiberDetailsArray.controls as UntypedFormGroup[];
  }

  protected addOpticalFiberRow(detail?: Partial<ManageTransmissionLineOpticalFiberPayload>): void {
    this.opticalFiberDetailsArray.push(this.createOpticalFiberGroup(detail));
  }

  protected removeOpticalFiberRow(index: number): void {
    if (this.modalMode === 'view') {
      return;
    }

    if (this.opticalFiberDetailsArray.length <= 1) {
      this.opticalFiberDetailsArray.at(0).reset(createManageTransmissionLineOpticalFiberFormValue(), { emitEvent: false });
      return;
    }

    this.opticalFiberDetailsArray.removeAt(index);
  }

  protected saveTransmissionLine(): void {
    if (this.modalMode === 'view') {
      return;
    }

    this.commitModalSubstationAutocompleteValue('from_substation_id');
    this.commitModalSubstationAutocompleteValue('to_substation_id');
    this.syncCrossborderParameterState();

    if (this.transmissionLineForm.invalid) {
      this.formSubmitted = true;
      this.submitErrorMessage = TRANSMISSION_LINE_REQUIRED_FIELDS_MESSAGE;
      this.transmissionLineForm.markAllAsTouched();
      this.markOpticalFiberRowsTouched();
      return;
    }

    this.submitErrorMessage = '';
    const payload = this.buildPayload();
    const apiRoute = this.modalMode === 'create' ? AuthEndPoints.ADD_TRANSMISSION_LINE : AuthEndPoints.EDIT_TRANSMISSION_LINE;
    const method = this.modalMode === 'create' ? ApiMethod.POST : ApiMethod.PATCH;

    this.saveTransmissionLineRecord(apiRoute, method, payload);
  }

  private saveTransmissionLineRecord(apiRoute: string, method: ApiMethod, payload: ManageTransmissionLinePayload): void {
    this.httpSvc
      .requestCall(apiRoute, method, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => this.handleTransmissionLineSaveSuccess(response),
        error: (error) => {
          this.submitErrorMessage = this.getApiErrorMessage(error) || TRANSMISSION_LINE_FAILED_SAVE_MESSAGE;
        },
      });
  }

  private handleTransmissionLineSaveSuccess(response: unknown): void {
    const lineId = this.extractLineId(response) ?? this.selectedTransmissionLine?.id ?? null;

    if (!this.selectedFile || lineId === null) {
      this.closeAndReloadTransmissionLines();
      return;
    }

    this.uploadAndAttachTransmissionLineFile(lineId);
  }

  private uploadAndAttachTransmissionLineFile(lineId: number): void {
    const file = this.selectedFile;
    if (!file) {
      this.closeAndReloadTransmissionLines();
      return;
    }

    this.uploadTransmissionLineFile(file)
      .pipe(
        switchMap((uploadRes) => {
          const filePath = this.extractFilePath(uploadRes);
          if (!filePath) {
            return of(null);
          }

          return this.httpSvc.requestCall(AuthEndPoints.EDIT_TRANSMISSION_LINE, ApiMethod.PATCH, {
            id: lineId,
            file_path: filePath,
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.closeAndReloadTransmissionLines(),
        error: (error) => {
          this.submitErrorMessage = this.getApiErrorMessage(error) || TRANSMISSION_LINE_FAILED_ATTACHMENT_MESSAGE;
          this.closeAndReloadTransmissionLines();
        },
      });
  }

  private closeAndReloadTransmissionLines(): void {
    this.closeTransmissionLineModal();
    this.loadTransmissionLines(this.pagination.page_no);
  }

  protected confirmDelete(): void {
    if (!this.selectedTransmissionLine) {
      return;
    }

    this.httpSvc
      .requestCall(AuthEndPoints.DELETE_TRANSMISSION_LINE, ApiMethod.DELETE, { id: this.selectedTransmissionLine.id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeDeleteModal();
          this.loadTransmissionLines(this.pagination.page_no);
        },
      });
  }

  protected trackByLineId(_index: number, item: ManageTransmissionLineItem): number {
    return item.id;
  }

  protected toggleSort(field: ManageTransmissionLineSortField): void {
    this.sortOrder = this.sortBy === field && this.sortOrder === 'asc' ? 'desc' : 'asc';
    this.sortBy = field;
    this.loadTransmissionLines(1);
  }

  protected isSortAscending(field: ManageTransmissionLineSortField): boolean { return this.sortBy === field && this.sortOrder === 'asc'; }
  protected isSortDescending(field: ManageTransmissionLineSortField): boolean { return this.sortBy === field && this.sortOrder === 'desc'; }

  protected isRequiredFieldInvalid(fieldName: string): boolean {
    const control = this.transmissionLineForm.get(fieldName);
    return Boolean(control?.hasError('required') && (control.touched || this.formSubmitted));
  }

  protected isOpticalFiberFieldInvalid(index: number, fieldName: string): boolean {
    const group = this.opticalFiberDetailsArray.at(index);
    const control = group?.get(fieldName);
    return Boolean(control?.hasError('required') && (control.touched || this.formSubmitted));
  }

  protected getRequiredFieldError(fieldName: string, message: string): string {
    return this.isRequiredFieldInvalid(fieldName) ? message : '\u00A0';
  }

  protected canShowPagination(): boolean {
    return this.pagination.total_records > 0;
  }

  protected getPaginationLabel(): string {
    if (!this.pagination.total_records) {
      return TRANSMISSION_LINE_NO_RECORDS_LABEL;
    }

    const start = (this.pagination.page_no - 1) * this.pagination.page_size + 1;
    const end = Math.min(this.pagination.page_no * this.pagination.page_size, this.pagination.total_records);
    return `${TRANSMISSION_LINE_SHOWING_RESULTS_LABEL} ${start} to ${end} of ${this.pagination.total_records} ${TRANSMISSION_LINE_RESULTS_LABEL}`;
  }

  protected getPaginationItems(): number[] {
    const totalPages = this.pagination.total_pages;
    if (!totalPages) {
      return [];
    }

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const currentPage = this.pagination.page_no;
    const pages: number[] = [];
    const addPage = (pageNo: number): void => {
      if (pageNo >= 1 && pageNo <= totalPages && !pages.includes(pageNo)) {
        pages.push(pageNo);
      }
    };

    addPage(1);
    const windowStart = Math.max(2, currentPage - 1);
    const windowEnd = Math.min(totalPages - 1, currentPage + 1);
    if (windowStart > 2) {
      pages.push(-1);
    }
    for (let page = windowStart; page <= windowEnd; page += 1) {
      addPage(page);
    }
    if (windowEnd < totalPages - 1) {
      pages.push(-1);
    }
    addPage(totalPages);
    return pages;
  }

  protected goToPreviousPage(): void {
    this.goToPage(this.pagination.page_no - 1);
  }

  protected goToNextPage(): void {
    this.goToPage(this.pagination.page_no + 1);
  }

  protected goToPage(pageNo: number): void {
    if (pageNo < 1 || pageNo > this.pagination.total_pages || pageNo === this.pagination.page_no) {
      return;
    }

    this.loadTransmissionLines(pageNo);
  }

  protected getSubstationName(substationId: number | null | undefined): string {
    if (substationId === null || substationId === undefined) {
      return '-';
    }

    return this.substationOptions.find((item) => item.id === substationId)?.name ?? '-';
  }

  protected getVoltageLevelLabel(voltageLevelId: number | null | undefined): string {
    if (voltageLevelId === null || voltageLevelId === undefined) {
      return '-';
    }

    return this.voltageLevelOptions.find((item) => item.id === voltageLevelId)?.name ?? '-';
  }

  protected isCrossBorder(value: boolean | null | undefined): string {
    if (value === null || value === undefined) {
      return '-';
    }

    if (value) {
      return 'Yes';
    }

    return 'No';
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  }

  private buildTransmissionLineFileName(prefix: string): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${prefix}_${year}${month}${day}_${hours}${minutes}${seconds}.xlsx`;
  }

  private resetOpticalFiberRows(details: Array<Partial<ManageTransmissionLineOpticalFiberPayload>> = []): void {
    while (this.opticalFiberDetailsArray.length > 0) {
      this.opticalFiberDetailsArray.removeAt(0);
    }

    if (details.length) {
      details.forEach((detail) => this.addOpticalFiberRow(detail));
      return;
    }

    this.addOpticalFiberRow();
  }

  private createOpticalFiberGroup(detail?: Partial<ManageTransmissionLineOpticalFiberPayload>): UntypedFormGroup {
    return this.fb.group({
      no_of_core: [detail?.no_of_core ?? '', [Validators.maxLength(10)]],
      length_of_optical_fiber_km: [detail?.length_of_optical_fiber_km ?? '', [Validators.maxLength(20)]],
      fiber_type: [detail?.fiber_type ?? '', [Validators.maxLength(255)]],
      fiber_use: [detail?.fiber_use ?? '', [Validators.maxLength(255)]],
      spark: [detail?.spark ?? '', [Validators.maxLength(255)]],
    });
  }

  private markOpticalFiberRowsTouched(): void {
    this.opticalFiberDetailsArray.controls.forEach((control) => {
      control.markAllAsTouched();
    });
  }

  private syncComputedFields(): void {
    const currentTransmissionLineName = String(this.transmissionLineForm.get('transmission_line_name')?.value ?? '').trim();
    const autogeneratedTransmissionLineName = this.buildAutogeneratedTransmissionLineName();
    const shouldUpdateTransmissionLineName =
      Boolean(autogeneratedTransmissionLineName) &&
      (!currentTransmissionLineName || currentTransmissionLineName === this.lastAutogeneratedTransmissionLineName);
    const noOfCircuit = this.toNumberOrNull(this.transmissionLineForm.get('no_of_circuit')?.value);
    const lengthKm = this.toNumberOrNull(this.transmissionLineForm.get('length_km')?.value);
    const perCircuitMva = this.toNumberOrNull(this.transmissionLineForm.get('per_circuit_mva')?.value);

    const circuitKm = noOfCircuit !== null && lengthKm !== null ? noOfCircuit * lengthKm : null;
    const totalCapacityMva = noOfCircuit !== null && perCircuitMva !== null ? noOfCircuit * perCircuitMva : null;

    if (shouldUpdateTransmissionLineName) {
      this.lastAutogeneratedTransmissionLineName = autogeneratedTransmissionLineName;
    }

    this.transmissionLineForm.patchValue(
      {
        ...(shouldUpdateTransmissionLineName ? { transmission_line_name: autogeneratedTransmissionLineName } : {}),
        circuit_km: this.toFormString(circuitKm),
        total_capacity_mva: this.toFormString(totalCapacityMva),
      },
      { emitEvent: false },
    );
  }

  private buildAutogeneratedTransmissionLineName(): string {
    const fromSubstationId = this.transmissionLineForm.get('from_substation_id')?.value;
    const toSubstationId = this.transmissionLineForm.get('to_substation_id')?.value;
    const voltageLevelId = this.transmissionLineForm.get('voltage_level_id')?.value;
    const fromSubstationName = this.findSubstationOptionBySelection(fromSubstationId, this.getSelectedSubstationCode('from_substation_id'))?.name ?? '';
    const toSubstationName = this.findSubstationOptionBySelection(toSubstationId, this.getSelectedSubstationCode('to_substation_id'))?.name ?? '';
    const voltageLevelLabel = this.voltageLevelOptions.find((item) => item.id === Number(voltageLevelId))?.name ?? '';
    return fromSubstationName && toSubstationName && voltageLevelLabel
      ? `${fromSubstationName}-${toSubstationName}-${voltageLevelLabel}`
      : '';
  }

  private extractLineId(response: unknown): number | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const value = (response as { value?: unknown }).value;
    if (!value || typeof value !== 'object') {
      return null;
    }

    const lineId = (value as Record<string, unknown>)['id'];
    const parsedId = Number(lineId);
    return Number.isFinite(parsedId) ? parsedId : null;
  }

  private buildPayload(): ManageTransmissionLinePayload {
    const formValue = this.transmissionLineForm.getRawValue() as ManageTransmissionLineFormValue;
    const opticalFiberDetails = this.opticalFiberDetailsArray.controls
      .map((control) => control.getRawValue() as ManageTransmissionLineOpticalFiberFormValue)
      .filter((detail) => this.hasOpticalFiberValue(detail))
      .map((detail) => ({
        no_of_core: this.toIntegerOrNull(detail.no_of_core),
        length_of_optical_fiber_km: this.toNumberOrNull(detail.length_of_optical_fiber_km),
        fiber_type: this.toNullableText(detail.fiber_type),
        fiber_use: this.toNullableText(detail.fiber_use),
        spark: this.toNullableText(detail.spark),
      }));

    const payload: ManageTransmissionLinePayload = {
      from_substation_id: formValue.from_substation_id,
      from_substation_code: this.getSelectedSubstationCode('from_substation_id'),
      to_substation_id: formValue.to_substation_id,
      to_substation_code: this.getSelectedSubstationCode('to_substation_id'),
      voltage_level_id: formValue.voltage_level_id,
      transmission_line_name: this.toNullableText(formValue.transmission_line_name),
      scada_code: this.toNullableText(formValue.scada_code),
      parameter: formValue.is_crossborder ? null : this.joinParameterValues(formValue.parameter),
      logsheet: Boolean(formValue.logsheet),
      no_of_circuit: this.toIntegerOrNull(formValue.no_of_circuit),
      length_km: this.toNumberOrNull(formValue.length_km),
      circuit_km: this.toNumberOrNull(formValue.circuit_km),
      per_circuit_ampere: this.toNumberOrNull(formValue.per_circuit_ampere),
      per_circuit_mva: this.toNumberOrNull(formValue.per_circuit_mva),
      total_capacity_mva: this.toNumberOrNull(formValue.total_capacity_mva),
      conductor_code_name: this.toNullableText(formValue.conductor_code_name),
      bundle: this.toNullableText(formValue.bundle),
      conductor_type: this.toNullableText(formValue.conductor_type),
      no_of_towers: this.toIntegerOrNull(formValue.no_of_towers),
      starting_tower_no: this.toNullableText(formValue.starting_tower_no),
      ending_tower_no: this.toNullableText(formValue.ending_tower_no),
      is_crossborder: Boolean(formValue.is_crossborder),
      optical_fiber_details: opticalFiberDetails,
    };

    if (this.modalMode === 'edit' && this.selectedTransmissionLine) {
      payload.id = this.selectedTransmissionLine.id;
    }

    if (this.filePathCleared) {
      payload.file_path = null;
    }

    return payload;
  }

  private uploadTransmissionLineFile(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpSvc.requestCall(AuthEndPoints.UPLOAD_TRANSMISSION_LINE_FILE, ApiMethod.POST, formData, false, { suppressToast: true });
  }

  private extractFilePath(response: unknown): string | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const value = (response as { value?: unknown }).value;
    if (!value || typeof value !== 'object') {
      return null;
    }

    const payload = value as Record<string, unknown>;
    const filePath = payload['file_path'];
    return typeof filePath === 'string' ? filePath : null;
  }

  private getFilePreviewUrl(): string | null {
    if (this.selectedFilePreviewUrl) {
      return this.selectedFilePreviewUrl;
    }

    if (this.existingFilePath) {
      if (/^https?:\/\//i.test(this.existingFilePath)) {
        return this.existingFilePath;
      }

      const normalizedPath = this.existingFilePath.startsWith('/') ? this.existingFilePath : `/${this.existingFilePath}`;
      return `${environment.apiBaseURL}${normalizedPath}`;
    }

    return null;
  }

  private getApiErrorMessage(error: unknown): string | null {
    if (!error || typeof error !== 'object') {
      return null;
    }

    const errorObj = error as { error?: unknown; message?: string };
    const payload = errorObj.error as { msg?: unknown; message?: unknown; detail?: unknown } | undefined;
    const message = payload?.msg ?? payload?.message ?? errorObj.message ?? payload?.detail;
    return typeof message === 'string' && message.trim() ? message.trim() : null;
  }

  private setSelectedFile(file: File | null): void {
    if (this.selectedFilePreviewUrl) {
      URL.revokeObjectURL(this.selectedFilePreviewUrl);
      this.selectedFilePreviewUrl = null;
    }

    this.selectedFile = file;
    this.selectedFileName = file ? file.name : '';
    this.selectedFilePreviewUrl = file ? URL.createObjectURL(file) : null;
    this.filePathCleared = false;
  }

  private enableTransmissionLineForm(): void {
    this.transmissionLineForm.enable({ emitEvent: false });
  }

  private disableTransmissionLineForm(): void {
    this.transmissionLineForm.disable({ emitEvent: false });
  }

  private hasOpticalFiberValue(detail: ManageTransmissionLineOpticalFiberFormValue): boolean {
    return Boolean(
      detail.no_of_core ||
      detail.length_of_optical_fiber_km ||
      detail.fiber_type ||
      detail.fiber_use ||
      detail.spark,
    );
  }

  private toFormString(value: ManageTransmissionLineFormFieldValue): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value);
  }

  private toNullableText(value: ManageTransmissionLineFieldValue): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    const normalized = String(value).trim();
    if (!normalized) {
      return null;
    }

    return normalized;
  }

  private toNumberOrNull(value: ManageTransmissionLineFieldValue): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    const normalized = String(value).trim();
    if (!normalized) {
      return null;
    }

    const numeric = Number(normalized);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private toIntegerOrNull(value: ManageTransmissionLineFieldValue): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    const normalized = String(value).trim();
    if (!normalized) {
      return null;
    }

    const numeric = Number.parseInt(normalized, 10);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private getParameterFilterValues(): string[] {
    const value = this.filterForm.get('parameter')?.value;
    return Array.isArray(value) ? value : [];
  }

  private getParameterFilterValue(): string | null {
    return this.joinParameterValues(this.getParameterFilterValues());
  }

  private splitParameterValues(value: string | null | undefined): string[] {
    if (!value) {
      return [];
    }
    return String(value)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  private joinParameterValues(values: string[] | null | undefined): string | null {
    const normalizedValues = (values ?? []).map((value) => value.trim()).filter(Boolean);
    return normalizedValues.length ? [...new Set(normalizedValues)].join(',') : null;
  }

  private buildQueryString(params: ManageTransmissionLineQueryParams): string {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '' || value === TRANSMISSION_LINE_ALL_FILTER_VALUE) {
        return;
      }

      queryParams.set(key, String(value));
    });

    const query = queryParams.toString();
    return query ? `?${query}` : '';
  }

  private extractPagination(value: unknown): ManageTransmissionLinePagination | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const pagination = (value as { pagination?: unknown }).pagination;
    if (!pagination || typeof pagination !== 'object') {
      return null;
    }

    const record = pagination as Record<string, unknown>;
    return {
      page_no: Number(record['page_no'] ?? 1),
      page_size: Number(record['page_size'] ?? 10),
      total_records: Number(record['total_records'] ?? 0),
      total_pages: Number(record['total_pages'] ?? 0),
    };
  }

  private getFileName(filePath: string): string {
    const parts = filePath.split('/');
    return parts[parts.length - 1] || filePath;
  }
}
